import { Pressable, View } from "@-/com.npmjs.react-native";
import { useCSSVariable } from "@-/com.npmjs.uniwind";
import { FrostedSurface } from "@-/dev.improve.haniverse.components.ui.frosted-surface";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { detailsTabBarIcon } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content._components.morpheme-info-panel-floating-tab-bar";
import type { ReactNode } from "react";
import type { MorphemeInfoTab } from "#morpheme-info-view";

/**
 * The iOS platter is 62pt tall: three tabs use 274pt, four use 360pt, inset by 21pt. A trailing
 * accessory sits 9pt to its right, centred on the platter, like the circle iOS reserves beside its
 * native tab bar.
 */
export function MorphemeInfoFrostedTabs({
  activeIndex,
  interactive = true,
  onSelect,
  tabs,
  testIDPrefix = "morpheme-info-tab",
  trailingAccessory,
  trailingAccessoryDiameter,
}: {
  activeIndex: number;
  interactive?: boolean;
  onSelect: (index: number) => void;
  tabs: readonly MorphemeInfoTab[];
  testIDPrefix?: string;
  trailingAccessory?: (diameter: number) => ReactNode;
  trailingAccessoryDiameter: number;
}) {
  const masteryColor = String(useCSSVariable("--stage-mastered") ?? "#f97316");
  return (
    <View
      className="absolute inset-x-5.25 bottom-5.25 flex-row items-center justify-center gap-x-2.25"
      pointerEvents={interactive ? "box-none" : "none"}
      testID="morpheme-info-tab-bar"
    >
      <FrostedSurface
        className="h-15.5 min-w-0 flex-1 flex-row rounded-full p-1"
        style={{ maxWidth: tabs.length * 86 + 16 }}
      >
        {tabs.map((tab, index) => {
          const selected = index === activeIndex;
          return (
            <Pressable
              accessibilityLabel={tab.title}
              accessibilityRole="tab"
              accessibilityState={{ disabled: !interactive, selected }}
              className={`min-w-0 flex-1 items-center justify-center gap-0.25 rounded-full active:opacity-70 ${selected ? "bg-foreground/10" : ""}`}
              disabled={!interactive}
              key={tab.segment}
              onPress={interactive ? () => onSelect(index) : undefined}
              testID={`${testIDPrefix}-${tab.segment}`}
            >
              {detailsTabBarIcon(tab.segment)({
                className: "text-foreground",
                color: selected ? masteryColor : undefined,
                focused: selected,
                size: 28,
              })}
              <Text
                className="text-center text-[10px] leading-3 font-medium text-foreground"
                numberOfLines={1}
                style={selected ? { color: masteryColor } : undefined}
              >
                {tab.title}
              </Text>
            </Pressable>
          );
        })}
      </FrostedSurface>
      {trailingAccessory?.(trailingAccessoryDiameter)}
    </View>
  );
}
